import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cliniqueo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and subscription gating
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cliniqueo_token');
      localStorage.removeItem('cliniqueo_user');
      localStorage.removeItem('cliniqueo_organisation');
      window.location.href = '/login';
    }
    // Handle subscription feature gating (403 with error details)
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        if (detail.error === 'feature_gated') {
          error.featureGated = true;
          error.gatedFeature = detail.feature;
          error.unlockPlan = detail.unlock_plan;
        } else if (detail.error === 'limit_reached') {
          error.limitReached = true;
          error.limitType = detail.limit_type;
        } else if (detail.error === 'subscription_expired') {
          error.subscriptionExpired = true;
        }
      }
    }
    // Handle subscription required (402 - read-only mode)
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'subscription_required') {
        error.subscriptionRequired = true;
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  validatePassword: (password) => api.post('/auth/validate-password', { password }),
  getInvitation: (token) => axios.get(`${API_BASE}/auth/invitation/${token}`),
  acceptInvitation: (token, password) => axios.post(`${API_BASE}/auth/accept-invitation`, { token, password }),
  forgotPassword: (email) => axios.post(`${API_BASE}/auth/forgot-password`, { email }),
  resetPassword: (token, password) => axios.post(`${API_BASE}/auth/reset-password`, { token, password }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  // Onboarding checklist UI preferences (Task 1)
  updateOnboardingPrefs: (prefs) => api.patch('/auth/me/onboarding-prefs', prefs),
  restoreChecklist: () => api.post('/auth/me/restore-checklist'),
};

// Organisation APIs
export const organisationAPI = {
  get: () => api.get('/organisation'),
  update: (data) => api.put('/organisation', data),
  getNotificationMeta: () => api.get('/organisation/notification-meta'),
};

// Notification APIs
export const notificationAPI = {
  getLogs: (params) => api.get('/notifications/logs', { params }),
  getTemplates: () => api.get('/notifications/templates'),
  preview: (data) => api.post('/notifications/preview', data),
};

// Stripe APIs
export const stripeAPI = {
  getConfig: () => api.get('/stripe/config'),
  connect: (data) => api.post('/stripe/connect', data),
  getConnectStatus: () => api.get('/stripe/connect/status'),
  getOnboardingLink: (params) => api.get('/stripe/connect/onboarding', { params }),
  getDashboardLink: () => api.get('/stripe/connect/dashboard'),
  createCheckout: (data) => api.post('/stripe/create-checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/stripe/checkout/status/${sessionId}`),
};

// Staff APIs
export const staffAPI = {
  getAll: () => api.get('/staff'),
  getOne: (id) => api.get(`/staff/${id}`),
  getRoles: () => api.get('/staff/roles'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  permanentDelete: (id) => api.delete(`/staff/${id}/permanent`, { data: { confirmation: 'DELETE' } }),
  restore: (id) => api.post(`/staff/${id}/restore`),
  resendInvite: (id) => api.post(`/staff/${id}/resend-invite`),
  adminResetPassword: (id) => api.post(`/staff/${id}/reset-password`),
  adminSetPassword: (id, password) => api.post(`/staff/${id}/set-password`, { password }),
};

// Client APIs
export const clientAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  importCSV: (formData) => api.post('/clients/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Service APIs
export const serviceAPI = {
  getAll: () => api.get('/services'),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  restore: (id) => api.post(`/services/${id}/restore`),
  permanentDelete: (id) => api.delete(`/services/${id}/permanent`, { data: { confirmation: 'DELETE' } }),
  bulkDeleteIncomplete: () => api.delete('/services/incomplete/bulk'),
  getPractitioners: () => api.get('/services/practitioners/list'),
  getSubcategories: (category) => api.get('/services/subcategories', { params: category ? { category } : {} }),
  importCSV: (formData) => api.post('/services/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Appointment APIs
export const appointmentAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, status) => api.patch(`/appointments/${id}/status`, { status }),
  getAvailability: (params) => api.get('/appointments/availability', { params }),
  getDensity: (params) => api.get('/appointments/density', { params }),
};

// Form Template APIs
export const formTemplateAPI = {
  getAll: (params) => api.get('/form-templates', { params }),
  get: (id) => api.get(`/form-templates/${id}`),
  getOne: (id) => api.get(`/form-templates/${id}`),
  create: (data) => api.post('/form-templates', data),
  update: (id, data) => api.put(`/form-templates/${id}`, data),
  delete: (id) => api.delete(`/form-templates/${id}`),
};

// Form Submission APIs
export const formSubmissionAPI = {
  getAll: (params) => api.get('/form-submissions', { params }),
  getOne: (id) => api.get(`/form-submissions/${id}`),
  create: (data) => api.post('/form-submissions', data),
  update: (id, data) => api.put(`/form-submissions/${id}`, data),
  complete: (id) => api.patch(`/form-submissions/${id}/complete`),
  getForAppointment: (aptId) => api.get(`/appointments/${aptId}/forms`),
};

// Payment APIs
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  getSummary: (date) => api.get('/payments/summary', { params: { date } }),
  refund: (paymentId, data) => api.post(`/payments/${paymentId}/refund`, data),
  getStripeStatus: () => api.get('/payments/stripe-status'),
};

// Treatment Course APIs
export const courseAPI = {
  // Templates
  listTemplates: (params) => api.get('/course-templates', { params }),
  getTemplate: (id) => api.get(`/course-templates/${id}`),
  createTemplate: (data) => api.post('/course-templates', data),
  updateTemplate: (id, data) => api.put(`/course-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/course-templates/${id}`),
  // Instances
  list: (params) => api.get('/courses', { params }),
  get: (id) => api.get(`/courses/${id}`),
  start: (data) => api.post('/courses', data),
  cancel: (id, data) => api.post(`/courses/${id}/cancel`, data),
  recordPayment: (id, data) => api.post(`/courses/${id}/payments`, data),
};

// Subscription APIs
export const subscriptionAPI = {
  get: () => api.get('/subscriptions'),
  getFeatures: () => api.get('/subscriptions/features'),
  getPlans: () => api.get('/subscriptions/plans'),
  upgrade: (data) => api.post('/subscriptions/upgrade', data),
  cancel: () => api.post('/subscriptions/cancel'),
  applyFoundingDiscount: () => api.post('/subscriptions/founding-discount'),
};

// Billing APIs (Stripe subscription billing)
export const billingAPI = {
  createCheckout: (data) => api.post('/billing/checkout', data),
  getStatus: () => api.get('/billing/status'),
  getCheckoutStatus: (sessionId) => api.get(`/billing/checkout/status/${sessionId}`),
  getHistory: () => api.get('/billing/history'),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getPendingForms: () => api.get('/dashboard/pending-forms'),
};

// Platform Admin APIs (God Mode)
export const platformAPI = {
  getTenants: (params) => api.get('/platform/tenants', { params }),
  getTenant: (id) => api.get(`/platform/tenants/${id}`),
  updateTenant: (id, data) => api.patch(`/platform/tenants/${id}`, data),
  suspendTenant: (id) => api.delete(`/platform/tenants/${id}`),
  archiveTenant: (id) => api.post(`/platform/tenants/${id}/archive`),
  restoreTenant: (id) => api.post(`/platform/tenants/${id}/restore`),
  hardDeleteTenant: (id, data) => api.delete(`/platform/tenants/${id}/permanent`, { data }),
  extendTrial: (id, data) => api.post(`/platform/tenants/${id}/extend-trial`, data),
  overrideFeature: (id, data) => api.post(`/platform/tenants/${id}/override-feature`, data),
  updateSubscription: (id, data) => api.post(`/platform/tenants/${id}/subscription`, data),
  activateTenant: (id) => api.post(`/platform/tenants/${id}/activate`),
  impersonate: (id) => api.post(`/platform/tenants/${id}/impersonate`),
  applyDiscount: (id, data) => api.post(`/platform/tenants/${id}/discount`, data),
  removeDiscount: (id) => api.delete(`/platform/tenants/${id}/discount`),
  getAnalytics: () => api.get('/platform/analytics'),
  getWatchLists: () => api.get('/platform/watch-lists'),
  getMRRSnapshots: () => api.get('/platform/mrr-snapshots'),
  getAuditLogs: (params) => api.get('/platform/audit-logs', { params }),
  getAuditFilterOptions: () => api.get('/platform/audit-logs/filter-options'),
  auditExportUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/platform/audit-logs/export.csv${qs ? '?' + qs : ''}`;
  },
  createBroadcast: (data) => api.post('/platform/broadcasts', data),
  getBroadcasts: () => api.get('/platform/broadcasts'),
  deleteBroadcast: (id) => api.delete(`/platform/broadcasts/${id}`),
};

// Broadcast APIs (for tenants)
export const broadcastAPI = {
  getActive: () => api.get('/broadcasts/active'),
};

// Registration APIs (no auth required)
export const registrationAPI = {
  registerTenant: (data) => api.post('/register/tenant', data),
};

// Onboarding APIs
export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
  getServiceTemplates: () => api.get('/onboarding/service-templates'),
  updateClinic: (data) => api.put('/onboarding/clinic', data),
  setupServices: (services) => api.post('/onboarding/services', services),
  setupTeam: (members) => api.post('/onboarding/team', members),
  setupBooking: (data) => api.put('/onboarding/booking', data),
  complete: () => api.post('/onboarding/complete'),
};

// Audit Log APIs
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
  getFilterOptions: () => api.get('/audit-logs/filter-options'),
  exportUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/audit-logs/export.csv${qs ? '?' + qs : ''}`;
  },
};

// Photo APIs
export const photoAPI = {
  upload: (data) => api.post('/photos', data),
  getAll: (params) => api.get('/photos', { params }),
  getOne: (id) => api.get(`/photos/${id}`),
  delete: (id) => api.delete(`/photos/${id}`),
};

// GDPR APIs
export const gdprAPI = {
  exportData: (clientId) => api.get(`/gdpr/export/${clientId}`),
  eraseData: (clientId, data) => api.post(`/gdpr/erase/${clientId}`, data),
  getConsentHistory: (clientId) => api.get(`/gdpr/consent/${clientId}`),
  marketingOptOut: (clientId) => api.post(`/gdpr/opt-out/${clientId}`),
};

// Public Booking APIs (no auth required)
export const publicAPI = {
  getDefaultOrganisation: () => axios.get(`${API_BASE}/public/organisation/default`),
  getOrganisation: (orgId) => axios.get(`${API_BASE}/public/organisation/${orgId}`),
  getServices: (orgId) => axios.get(`${API_BASE}/public/services/${orgId}`),
  getPractitioners: (orgId, serviceId) => 
    axios.get(`${API_BASE}/public/practitioners/${orgId}`, { params: { service_id: serviceId } }),
  getAvailability: (orgId, practitionerId, date, serviceId) => 
    axios.get(`${API_BASE}/public/availability/${orgId}`, { params: { practitioner_id: practitionerId, date, service_id: serviceId } }),
  getAvailableDates: (orgId, practitionerId, serviceId, weeks) =>
    axios.get(`${API_BASE}/public/available-dates/${orgId}`, { params: { practitioner_id: practitionerId, service_id: serviceId, weeks } }),
  clientLookup: (data) => axios.post(`${API_BASE}/public/client-lookup`, data),
  createBooking: (data) => axios.post(`${API_BASE}/public/booking`, data),
  getBookingPaymentStatus: (appointmentId) => axios.get(`${API_BASE}/public/booking/${appointmentId}/payment-status`),
  retryBookingPayment: (appointmentId) => axios.post(`${API_BASE}/public/booking/${appointmentId}/retry-payment`),
  getForm: (submissionId) => axios.get(`${API_BASE}/public/form/${submissionId}`),
  submitForm: (submissionId, data) => axios.put(`${API_BASE}/public/form/${submissionId}`, data),
};

// Platform admin APIs (super-admin only) for Build 6a Stripe tooling
export const platformStripeAPI = {
  listEvents: (params) => api.get('/platform/stripe-events', { params }),
  cleanupEmptyOrgs: () => api.post('/platform/cleanup-empty-orgs'),
  cleanupOrphanStripeAccounts: (confirm = false) =>
    api.post(`/platform/cleanup-orphan-stripe-accounts${confirm ? '?confirm=true' : ''}`),
};

export default api;
